import { useEffect, useRef } from 'react';
import { useAppDispatch } from '@/app/hooks';
import { setUiLocked } from './waiterSlice';

// Spec §3.1: inactivity timer → uiLocked=true → PinPad. Any of these events
// counts as "waiter activity" and pushes the auto-lock deadline back out.
const ACTIVITY_EVENTS = ['pointerdown', 'keydown', 'touchstart', 'wheel'] as const;

/**
 * Auto-locks the waiter UI back to the PIN pad after `idleLockSeconds` of no
 * activity. A no-op while `enabled` is false (e.g. already locked, or no
 * waiter auth yet) or while `idleLockSeconds` is unset/non-positive.
 *
 * `onIdle` is an optional extra side effect run right after the lock fires —
 * e.g. WaiterBar uses it to also record the served table and navigate back
 * to `/serveur` (the ordering screen has nothing else that reacts to
 * `uiLocked`, unlike WaiterShell where PinPad is already on-screen).
 */
export function useIdleLock(
  idleLockSeconds: number | undefined,
  enabled: boolean,
  onIdle?: () => void,
): void {
  const dispatch = useAppDispatch();
  const timeoutRef = useRef<number | null>(null);

  useEffect(() => {
    if (!enabled || !idleLockSeconds || idleLockSeconds <= 0) return undefined;

    const lock = () => {
      dispatch(setUiLocked(true));
      onIdle?.();
    };

    const resetTimer = () => {
      if (timeoutRef.current !== null) window.clearTimeout(timeoutRef.current);
      timeoutRef.current = window.setTimeout(lock, idleLockSeconds * 1000);
    };

    resetTimer();
    ACTIVITY_EVENTS.forEach((evt) => window.addEventListener(evt, resetTimer, { passive: true }));

    return () => {
      if (timeoutRef.current !== null) window.clearTimeout(timeoutRef.current);
      ACTIVITY_EVENTS.forEach((evt) => window.removeEventListener(evt, resetTimer));
    };
  }, [dispatch, idleLockSeconds, enabled, onIdle]);
}
